import { MobXProviderContext } from "mobx-react";
import { useContext, useMemo } from "react";
import { buildSectionPath, getCurriculumMetadata, isSectionPath, networkDocumentKey } from "../../shared/shared";
// https://mobx-react.js.org/recipes-migration
export function useStores() {
    return useContext(MobXProviderContext).stores;
}
export function useAppConfig() {
    return useStores().appConfig;
}
export function useAppMode() {
    return useStores().appMode;
}
export function useClassStore() {
    return useStores().class;
}
export function useDBStore() {
    return useStores().db;
}
export function useDemoStore() {
    return useStores().demo;
}
export function useDocumentFromStore(key) {
    const { documents, networkDocuments } = useStores();
    return key
        ? documents.getDocument(key) || (networkDocuments === null || networkDocuments === void 0 ? void 0 : networkDocuments.getDocument(key))
        : undefined;
}
export function useDocumentMetadataFromStore(key) {
    const document = useDocumentFromStore(key);
    return document === null || document === void 0 ? void 0 : document.metadata;
}
export function useDocumentOrCurriculumMetadata(key) {
    const documentMetadata = useDocumentMetadataFromStore(key);
    return useMemo(() => {
        return isSectionPath(key) ? getCurriculumMetadata(key) : documentMetadata;
    }, [documentMetadata, key]);
}
export function useCurriculumOrDocumentContent(key) {
    var _a;
    const curriculumContentFromPath = useCurriculumContentFromPath(key);
    const documentContentFromKey = (_a = useDocumentFromStore(key)) === null || _a === void 0 ? void 0 : _a.content;
    return isSectionPath(key) ? curriculumContentFromPath : documentContentFromKey;
}
export function useCurriculumContentFromPath(key) {
    var _a, _b;
    const { section, facet } = getCurriculumMetadata(key) || {};
    const { problem, teacherGuide } = useStores();
    if (facet === "guide") {
        return teacherGuide && section ? (_a = teacherGuide.getSectionById(section)) === null || _a === void 0 ? void 0 : _a.content : undefined;
    }
    return problem && section
        ? (_b = problem.getSectionById(section)) === null || _b === void 0 ? void 0 : _b.content
        : undefined;
}
export function useTypeOfTileInDocumentOrCurriculum(key, tileId) {
    const { documents, networkDocuments } = useStores();
    if (!key || !tileId)
        return;
    if (isSectionPath(key)) {
        // for curriculum documents, tileId is `${section}_${tileType}_${index}`
        const execResult = /.*_(.+)_\d+$/.exec(tileId);
        return execResult === null || execResult === void 0 ? void 0 : execResult[1];
    }
    return documents.getTypeOfTileInDocument(key, tileId) ||
        (networkDocuments === null || networkDocuments === void 0 ? void 0 : networkDocuments.getTypeOfTileInDocument(key, tileId));
}
export function useFeatureFlag(feature) {
    return useAppConfig().isFeatureSupported(feature);
}
export function useGroupsStore() {
    return useStores().groups;
}
export function useLocalDocuments() {
    return useStores().documents;
}
export function useNetworkDocuments() {
    return useStores().networkDocuments;
}
/**
 *
 * @param documentKey
 * @param userId if this is passed the current user and their network is
 * ignored. This is useful for teachers to generate paths to student documents
 * @returns
 */
export function useNetworkDocumentKey(documentKey, userId) {
    const user = useUserStore();
    if (userId) {
        return networkDocumentKey(userId, documentKey);
    }
    else {
        return networkDocumentKey(user.id, documentKey, user.network);
    }
}
export function useProblemPath() {
    return useStores().problemPath;
}
export function useProblemPathWithFacet(facet) {
    const problemPath = useProblemPath();
    return buildSectionPath(problemPath, undefined, facet);
}
export function useProblemStore() {
    return useStores().problem;
}
export function useSettingFromStores(key, group) {
    return useAppConfig().getSetting(key, group);
}
export function useSharedSelectionStore() {
    return useStores().selection;
}
export function usePersistentUIStore() {
    return useStores().persistentUI;
}
export function useUIStore() {
    return useStores().ui;
}
export function useUserStore() {
    return useStores().user;
}
export function useSeismicQueryService() {
    return useStores().seismicQueryService;
}
//# sourceMappingURL=data:application/json;base64,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